/**
 * People — Contacts and Organizations, one destination.
 *
 * They were two navigation items sitting under a "Records" heading, which asked
 * the client to know in advance whether the person they were looking for was
 * filed as a person or as a business. They are one question, so they are one
 * page with two tabs; Contacts leads because it is what the portal is mostly
 * about and what almost every lead resolves to.
 *
 * The routes are unchanged — /contacts and /organizations, with their detail
 * paths — so every existing link into either record type still lands correctly.
 * This view is the shell that makes them read as one place, and the sidebar's
 * People row stays lit across both (see `matchPrefixes` in platform/navigation).
 */

import { useLocation, useNavigate } from 'react-router-dom';
import { Building2, Users } from 'lucide-react';
import { ListTabs, useIsSplitView, usePageHeader } from '@command-center/ui-system';
import { ContactsView } from './ContactsView';
import { OrganizationsView } from './OrganizationsView';

type PeopleTab = 'contacts' | 'organizations';

export function PeopleView() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const tab: PeopleTab = pathname.startsWith('/organizations') ? 'organizations' : 'contacts';

  /*
    One page identity for both tabs. The child views deliberately do NOT
    register their own: two `usePageHeader` registrations on one screen race
    each other, and the loser's cleanup blanks the header.
  */
  usePageHeader(
    { title: 'People', subtitle: 'Contacts, and the organizations behind them.' },
    [],
  );

  /*
    Below the split breakpoint an open record IS the screen, so a strip offering
    a jump to the other record type would be the first thing on a detail page —
    the same reason the Form Entries and Calls feeds hide theirs.
  */
  const recordOpen = /^\/(contacts|organizations)\/[^/]+/.test(pathname);
  const tabsVisible = useIsSplitView() || !recordOpen;

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {tabsVisible && (
        <ListTabs
          label="People"
          active={tab}
          onChange={(next) => navigate(next === 'organizations' ? '/organizations' : '/contacts')}
          tabs={[
            { id: 'contacts', label: 'Contacts', icon: <Users className="h-4 w-4" /> },
            { id: 'organizations', label: 'Organizations', icon: <Building2 className="h-4 w-4" /> },
          ]}
        />
      )}
      {tab === 'organizations' ? <OrganizationsView /> : <ContactsView />}
    </div>
  );
}
